import { useState } from 'react';
import { Download, FileDown } from 'lucide-react';
import { CertificateImage, downloadCertificate, formatCertDate, printCertificate, type CertificateData } from './certificate';

export interface RecordEventLite { type: string; at: string; title: string; detail: string; certificate: boolean; ref: string; cert?: { headline: string; label?: string; value?: string }; accountId?: string | null; login?: string | null }
export interface RecordLite {
  name: string; firm: string; template?: { heading: string; signatory: string; signatoryTitle: string; footer: string } | null;
  accounts?: { id: string; login: string; logins: string[]; name: string; status: string; phase: string }[];
  events: RecordEventLite[];
}
const day = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Certificates grouped by trading account: a switcher for each account (and "General" for welcome and awards),
 * the chosen certificate large with download buttons, and the rest as thumbnails. Every certificate says which account it is for.
 */
export function CertificateBrowser({ data, logoUrl, empty }: { data: RecordLite; logoUrl?: string | null; empty?: React.ReactNode }) {
  const certs = data.events.filter((e) => e.certificate && e.cert);
  const accounts = data.accounts ?? [];
  const general = certs.filter((c) => !c.accountId);
  const [acc, setAcc] = useState<string>('all');
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shown = acc === 'all' ? certs : acc === 'general' ? general : certs.filter((c) => c.accountId === acc);
  const open = shown.find((c) => c.ref === pick) ?? shown[0];
  const tpl = data.template ?? { heading: '', signatory: data.firm, signatoryTitle: '', footer: '' };
  const accName = (id?: string | null) => (data.accounts ?? []).find((a) => a.id === id)?.name;
  const toData = (e: RecordEventLite): CertificateData => ({
    headline: e.cert!.headline, name: data.name, label: e.cert!.label, value: e.cert!.value, date: formatCertDate(e.at),
    signatory: tpl.signatory, signatoryTitle: tpl.signatoryTitle,
    id: e.login ? `ACC ${e.login}` : e.ref.replace(/[^a-z0-9]/gi, '').slice(0, 10).toUpperCase(), logoUrl: logoUrl ?? undefined,
  });
  if (!certs.length) return <>{empty ?? <p className="muted">No certificates yet.</p>}</>;
  return (
    <div className="cb">
      {accounts.length > 0 && (
        <div className="cb-accs" role="tablist" aria-label="Account">
          <button role="tab" aria-selected={acc === 'all'} className={acc === 'all' ? 'on' : ''} onClick={() => { setAcc('all'); setPick(null); }}><b>All certificates</b><small>{certs.length}</small></button>
          {accounts.map((a) => {
            const n = certs.filter((c) => c.accountId === a.id).length;
            return <button key={a.id} role="tab" aria-selected={acc === a.id} className={acc === a.id ? 'on' : ''} onClick={() => { setAcc(a.id); setPick(null); }}><b>{a.name}</b><small className="mono">#{a.login} · {n}</small></button>;
          })}
          {general.length > 0 && <button role="tab" aria-selected={acc === 'general'} className={acc === 'general' ? 'on' : ''} onClick={() => { setAcc('general'); setPick(null); }}><b>General</b><small>{general.length}</small></button>}
        </div>
      )}
      {!open && <p className="cb-none">No certificates for this account yet. Pass a phase, get funded or receive a payout and it appears here.</p>}
      {open && (
        <div className="cert-layout">
          <CertificateImage data={toData(open)} />
          <div className="cert-side">
            <h3 style={{ margin: 0 }}>{open.title}</h3>
            {open.accountId ? <p className="cb-for">For account <b className="mono">#{open.login}</b>{accName(open.accountId) ? ` · ${accName(open.accountId)}` : ''}</p> : <p className="cb-for">Not tied to one account</p>}
            <p className="muted" style={{ margin: 0 }}>{open.detail}, {day(open.at)}</p>
            <button className="btn primary" disabled={busy} onClick={async () => { setBusy(true); await downloadCertificate(toData(open), `${open.cert!.headline}-certificate-${open.login ?? ''}-${data.name}`.replace(/\s+/g, '-')); setBusy(false); }}><Download />Download image</button>
            <button className="btn ghost" onClick={() => printCertificate(toData(open))}><FileDown />Save as PDF</button>
            {tpl.footer && <p className="fine">{tpl.footer}</p>}
          </div>
        </div>
      )}
      {shown.length > 1 && (
        <div className="cert-thumbs">{shown.map((c) => (
          <button key={c.ref} className={c.ref === open?.ref ? 'on' : ''} onClick={() => setPick(c.ref)}>
            <CertificateImage data={toData(c)} /><span>{c.cert!.headline}</span><small>{c.login ? `#${c.login} · ` : ''}{day(c.at)}</small>
          </button>
        ))}</div>
      )}
    </div>
  );
}
